import { createLikeableSchema, LikeableEntity } from 'mk2/containers/Like/Like.schemas';
import { Entity, PhotoEntity, PhotoSchema, UserEntity, UserSchema } from 'mk2/schemas';
import { schema } from 'normalizr';

export type PageRef = number | 'last';

export interface CategoryEntity extends Entity {
    orderIndex: number;
    name: string;
    slug: string;
    coverPhoto: PhotoEntity;
    id: number;
}

export interface TopicEntity extends Entity {
    slug: string;
    category: CategoryEntity;
    name: string;
    description: string;
    moderators: UserEntity[];
    coverPhoto: PhotoEntity;
    isCommercial: boolean;
    isSMM: boolean;
    deletedAt: string;
    deletedReason: string;
    deletedBy: UserEntity;
    visibility: number;
}

export interface TopicModeratorInvitationEntity extends Entity {
    invitee: UserEntity;
    inviter: UserEntity;
    createdAt: string;
    deleteAt: string;
    acceptedAt: string;
}

export interface TopicModeratorsDetailsEntity extends Entity {
    allModerators: Array<{
        user: UserEntity;
        createdAt: string;
        deletedAt: string;
    }>;
    pendingInvitations: TopicModeratorInvitationEntity[];
}

export interface MicroPostEntity extends Entity {
    heading: string;
    comments: number;
    slug: string;
}

export interface PostEntity extends Entity {
    topic: TopicEntity;
    author: UserEntity;
    created: string;
    heading: string;
    perex: string;
    photos: PhotoEntity[];
    stickerId: number;
    likeable: LikeableEntity;
    comments: number;
    sticky: boolean;
    locked: boolean;
    lastMessageTime: string;
    slug: string;
    popularAddedOn: string;
    noindex: boolean;
    isNoAds: boolean;
    canBeDeleted: boolean;
    deletedAt?: string;
    deletedByAuthor?: boolean;
    deletedReason?: string;
    deletedBy?: UserEntity;
    counsellingAdminApprovedTime: string;
    testing?: ProductTestingEntity;
    contestEnded?: boolean;
}

export interface ProductTestingEntity extends Entity {
    name: string;
    isActive: boolean;
    trackedHashtag: string;
}

export interface ContestEntity extends Entity {
    name: string;
    isDraft: boolean;
    endDate: string;
    seeAnswers: boolean;
    maxNotifs: number;
    postId: number;
}

export interface CampaignEntity extends Entity {
    name: string;
    user: UserEntity;
    logoSrc: string;
    logoWidth: number;
    logoHeight: number;
    avatar: string;
    greeting: string;
}

export interface CounsellingEntity extends Entity {
    counsellor: UserEntity;
    counsellorAvatar: string;
    counsellorName: string;
    counsellorSignature: string;
    isLocked: boolean;
    lockedMessage: string;
}

export interface ArchivedCampaignsEntity extends Entity {
    topic: TopicEntity;
    date: Date;
    isActive: boolean;
}

export const CategorySchema = new schema.Entity('categories');

export const TopicSchema = new schema.Entity('topics', {
    moderators: [UserSchema],
    coverPhoto: PhotoSchema,
    deletedBy: UserSchema,
});

export const TopicModeratorInvitationSchema = new schema.Entity('topicModeratorInvitations', {
    invitee: UserSchema,
    inviter: UserSchema,
});

export const TopicModeratorsDetailsSchema = new schema.Entity('topicModeratorsDetails', {
    allModerators: [new schema.Object({
        user: UserSchema,
    })],
    pendingInvitations: [TopicModeratorInvitationSchema],
});

export const MicroPostSchema = new schema.Entity('microPosts', {
});

export const TestingSchema = new schema.Entity('testings');

export const PostSchema = new schema.Entity('posts', {
    likeable: createLikeableSchema('thread'),
    topic: TopicSchema,
    author: UserSchema,
    photos: [PhotoSchema],
    testing: TestingSchema,
});

export const ContestSchema = new schema.Entity('contests');

export const ArchivedCampaignsSchema = new schema.Entity('archivedCampaigns', {
    topic: TopicSchema,
});

// TODO Refactor, because it's confusing for most of us, it's like Comment Schema
// TODO but because Like needs entity matched with DB, we need to write new schema
export const MessageSchema = new schema.Entity('messages', {
    authorUser: UserSchema,
    targetOwner: UserSchema,
    photos: [PhotoSchema],
    likeable: createLikeableSchema('forum-message'),
});

export interface ComplaintsStatistics {
    count: number;
    topicId: number;
}
export interface ComplaintsStatisticsPerPost {
    [postId: number]: ComplaintsStatistics;
}
